import type { MechanicDefinition } from "./types";

/**
 * Quest System mechanic — manages quest_definitions and adventure_quests
 * for game-api powered strategy/RPG games.
 *
 * This mechanic provides UI visibility and configuration for the quest system
 * used by the game-api edge function. Quest persistence is handled by the
 * game-api dialogue tools, not by the adventure narrator pipeline.
 */
export const questSystem: MechanicDefinition = {
  id: "quest_system",
  name: "Quest System (Game API)",
  description: "Structured quest chains for strategy/RPG games powered by the game-api. Quests are persisted as DB rows with objectives, rewards, prerequisites, and NPC-giver links. Supports linear chains with auto-unlock on completion.",
  icon: "ScrollText",
  category: "systems",
  subGroup: "strategy",
  aiHints: {
    purpose: "Provides structured quest persistence for the game-api. Quests are stored as quest_definitions (templates) and adventure_quests (runtime instances). NPCs can offer, advance, and complete quests during dialogue.",
    whenToUse: "Any world that will be consumed by the game-api for strategy/RPG games with NPC dialogue and quest chains.",
    synergies: ["reputation", "economy", "reactive_world", "dialogue_system"],
    conflicts: [],
    prerequisites: [],
    tags: ["quests", "missions", "objectives", "game-api", "strategy", "rpg", "chains"],
    complexity: 3,
    exampleConfig: {
      quest_chain_max_depth: 5,
      auto_unlock_next: true,
      quest_board_enabled: true,
    },
    stateVariables: ["_active_quests_summary", "_npc_dispositions"],
  },
  configSchema: [
    {
      key: "quest_chain_max_depth",
      label: "Max Chain Depth",
      type: "number",
      default: 5,
      description: "Maximum number of quests in a linear chain",
    },
    {
      key: "auto_unlock_next",
      label: "Auto-Unlock Next Quests",
      type: "boolean",
      default: true,
      description: "Automatically make the next quest in a chain available when the current one completes",
    },
    {
      key: "allow_radiant_quests",
      label: "Allow Radiant Quests",
      type: "boolean",
      default: false,
      description: "Enable repeatable, procedurally-generated radiant quests",
    },
    {
      key: "npc_quest_gating",
      label: "NPC Quest Gating",
      type: "select",
      options: [
        { value: "disposition", label: "Disposition-based — NPCs offer quests based on how they feel about the player" },
        { value: "always", label: "Always available — NPCs always offer their quests" },
        { value: "story", label: "Story-gated — Quests unlocked by story progression only" },
      ],
      default: "disposition",
    },
  ],
  systemPromptSection: (config) => {
    const maxDepth = config.quest_chain_max_depth || 5;
    const autoUnlock = config.auto_unlock_next !== false;
    const radiant = config.allow_radiant_quests === true;
    const gating = config.npc_quest_gating || "disposition";

    return `## QUEST SYSTEM (Game API)
- Quests are stored as quest_definitions (templates in the world) and adventure_quests (runtime instances).
- Max chain depth: ${maxDepth} quests per chain.
- ${autoUnlock ? "Next quest in chain auto-unlocks on completion." : "Next quests must be manually unlocked."}
- ${radiant ? "Radiant (repeatable) quests are enabled." : "Only hand-crafted quests."}
- NPC quest gating: ${gating === "disposition" ? "NPCs offer quests based on their disposition toward the player (Friendly+ to offer, Allied+ for best quests)." : gating === "always" ? "NPCs always offer their quests regardless of disposition." : "Quests unlocked by story progression only."}

### HOW TO CREATE QUEST CHAINS
1. Use the World Builder to create NPCs, locations, and factions
2. Use \`generate_quest_chain\` to create a linear chain of quest_definitions
3. Use \`generate_starting_scenario\` to set up the initial game state
4. The game-api reads these at init time and makes starting quests available

### DIALOGUE TOOLS (available during game-api NPC conversations)
- \`offer_quest\` — NPC offers a quest to the player
- \`advance_quest_objective\` — Progress a specific objective
- \`complete_quest\` — Mark quest complete, deliver rewards, unlock next
- \`fail_quest\` — Mark quest failed with consequences`;
  },
  tools: [],
  widgets: [],
};
